import { useField, useFormikContext } from 'formik'
import { MdContentCopy, MdDelete } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { FormatOptionLabelMeta } from 'react-select'
import type { IEngineFormValues } from '~/types/arbitration'

import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import { copyText } from '~/utils/navigator'
import { truncate } from '~/utils/string'

import './styles.scss'

interface Props {
  navigationGraphName: string
  navigationGraphOptions: IOption[]
  onDelete: VoidFunction
  placementName: string
  placementOptions: IOption[]
}

const formatNavigationGraphOption = (data: unknown, metadata: FormatOptionLabelMeta<unknown>) => {
  const { label, value } = data as IOption
  return (
    <div className="navigation-graph-option">
      <div className="navigation-graph-option__label">{label}</div>
      {metadata.context === 'menu' ? <div className="navigation-graph-option__id">{value}</div> : null}
    </div>
  )
}

const PlacementCard = ({
  navigationGraphName,
  navigationGraphOptions,
  onDelete,
  placementName,
  placementOptions,
}: Props): ReactElement => {
  const { values } = useFormikContext<IEngineFormValues>()

  const [, { value: placement }] = useField<IOption | undefined>(placementName)

  const [, { value: navigationGraph }] = useField<IOption | undefined>(navigationGraphName)

  const availablePlacementOptions = placementOptions.filter(({ value }) => {
    return !values.placements.find(({ placement }) => placement?.value === value)
  })

  const handleNavigationGraphLinkClick = () => {
    navigationGraph?.value && window.open(`/dynamic-screens/navigation-graphs/${navigationGraph.value}`, '_blank')
  }

  return (
    <div className="engine-placement-card">
      <div className="engine-placement-card__placement">
        <SelectInput label="Placement" name={placementName} options={availablePlacementOptions} />
        {placement ? (
          <Button ghost onClick={() => copyText(placement.value, 'Placement ID copied to clipboard.')} type="button">
            <MdContentCopy size={14} />
          </Button>
        ) : (
          <div className="empty-space" />
        )}
      </div>
      <div className="engine-placement-card__navigation-graph">
        <SelectInput
          formatOptionLabel={formatNavigationGraphOption}
          label="Navigation graph"
          menuClassName="navigation-graph-select-menu"
          name={navigationGraphName}
          options={navigationGraphOptions}
        />
        {navigationGraph ? (
          <>
            <LinkButton
              backgroundColor="light"
              label={truncate(navigationGraph.value)}
              onClick={handleNavigationGraphLinkClick}
              type="external"
            />
            <Button
              ghost
              onClick={() => copyText(navigationGraph.value, 'Navigation graph ID copied to clipboard.')}
              type="button"
            >
              <MdContentCopy size={14} />
            </Button>
          </>
        ) : (
          <div className="empty-space" />
        )}
      </div>
      <Button className="delete-button" ghost onClick={onDelete} type="button">
        <MdDelete size={16} />
      </Button>
    </div>
  )
}

export default PlacementCard
